import { appSync } from './app_sync.js';
import { error, log } from './logger.js';
import { navigation } from './navigation.js';
import { services } from './services.js';
import { session } from './session.js';
import { settings } from './settings.js';
import { logFocusEvents } from '../util/util.js';
import { window } from '../window.js';

const kSuccess = 200;

class App {
  navigation = navigation;
  services = services;
  session = session;
  settings = settings;

  BadCredentials = 'BAD_CREDENTIALS';
  BadPasswordRecoveryHash = 'INVALID_PASSWORD_RECOVERY_HASH';
  UnexpectedFailure = 'UNEXPECTED_FAILURE';
  Success = '';

  initialize({ LoginDialog, dataRequest }) {
    this.#LoginDialog = LoginDialog;
    this.services.setRequest(dataRequest);

    window.addEventListener('beforeunload', () => {
      this.shutdown = true;
      appSync.shutdown();
    });

    logFocusEvents();
  }

  async restoreSession() {
    let { sessionId, user } = await services.account.identify();
    session.id = sessionId;
    if (user) {
      this.#onAuthenticated(user);
    }

    log(`[load] user session restored to ${(user && user.id) || null}`);
    return navigation.restoreSession(user);
  }

  authenticate() {
    return this.authenticatePreventDefaultIfAuthNeeded();
  }

  authenticatePreventDefaultIfAuthNeeded(ev) {
    if (session.user) {
      return Promise.resolve(true);
    }
    ev?.preventDefault();
    return this.#LoginDialog.show();
  }

  async authcall(func) {
    let authenticated = await this.authenticate();
    if (authenticated) {
      func();
    }
  }

  login(credentials) {
    return this.#wrap(
      services.account.login(credentials),
      [this.BadCredentials],
      this.#onAuthenticated
    );
  }

  logout(redirectPath = 'intro') {
    session.user = null;
    return this.#wrap(services.account.logout(), [], () =>
      window.location.replace(`${window.location.origin}/${redirectPath}`)
    );
  }

  signup(credentials) {
    return this.#wrap(
      services.account.signup(credentials),
      [],
      this.#onAuthenticated
    );
  }

  contactUs({ subject, message, name, email }) {
    return this.#wrap(
      services.account.contactUs({
        name: name ?? session.user?.name,
        email: email ?? session.user?.email,
        subject,
        message,
      })
    );
  }

  recoverPassword(email) {
    return this.#wrap(services.account.recoverPassword(email));
  }

  cancelPasswordRecovery(hash) {
    return this.#wrap(services.account.cancelPasswordRecovery(hash));
  }

  checkPasswordRecoveryHash(hash) {
    return this.#wrap(
      services.account.checkPasswordRecoveryHash(hash),
      [this.BadPasswordRecoveryHash],
      valid => (valid ? this.Success : this.BadPasswordRecoveryHash)
    );
  }

  resetPassword(hash, newPwd) {
    return this.#wrap(services.account.resetPassword({ hash, pwd: newPwd }));
  }

  #onAuthenticated(user) {
    log(`[account] '${user.name}' <${user.email}> logged in`);

    session.user = {
      id: user.id,
      name: user.name,
      email: user.email,
      avatar: user.avatar,
    };

    document.body.setAttribute('logged-in', 'true');
    if (user.isAdmin) {
      document.body.setAttribute('user-admin', 'true');
    }
    if (user.isModerator) {
      document.body.setAttribute('user-moderator', 'true');
    }

    navigation.setPanelOnLogin();

    // Accept share if needed. Note, it will reload trips.
    const { inviteHash } = navigation.params;
    if (inviteHash) {
      navigation.tripPlanning.acceptShare(inviteHash);
    } else {
      navigation.tripPlanning.loadTrips();
    }
  }

  async #wrap(promise, errorCodes, onsuccess) {
    let result;
    try {
      result = await promise;
    } catch (e) {
      error(`[app] request failure`, e);
      return this.UnexpectedFailure;
    }

    let status = typeof result == 'object' && result?.status;
    let response = status ? result.response : result;

    if (status && status != kSuccess) {
      return this.UnexpectedFailure;
    }

    if (response?.errorCode) {
      return (
        errorCodes?.find(code => code == response.errorCode) ??
        this.UnexpectedFailure
      );
    }
    try {
      return (await onsuccess?.(response)) ?? this.Success;
    } catch (e) {
      error(`[app] request callback failure`, e);
      return this.UnexpectedFailure;
    }
  }

  #LoginDialog;
}

export const app = new App();
